import { Grid } from '@mui/material';
import CardAnalytics from 'views/Production/components/CardAnalytics/CardAnalytics';
import type {
  utilizationFleetScore,
  FleetSummary,
  chartDataResponseItem,
} from 'types/utilization.types';
import { convertsecondsToHours } from 'views/Utilization/utils';
import useShiftConfig from 'hooks/useShiftConfig';
import { formatShiftHour } from 'utils/helpers/customerShiftHelpers';
import { truncateTo2dp } from 'utils/helpers/general';
import {
  evaluateNumber,
  numberEvaluationKeyMappings,
  getFleetKpiThreshold,
} from 'views/FleetOverview/components/AssetCardsV2/utils';
import { useEffect, useMemo } from 'react';
import {
  getDiffForUtilization,
  isNegative,
  returnAnalysis,
} from 'utils/fuelAnalytics';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { useTranslation } from 'react-i18next';

dayjs.extend(utc);
dayjs.extend(timezone);

interface Props {
  isLoading: boolean;
  assetOverview: any;
  summary: FleetSummary;
  score: utilizationFleetScore;
  data: chartDataResponseItem[];
  assetType: 'total' | 'Haul Truck' | 'Loader' | 'Others';
  isSelected: string;
  aggregation?: string;
  shiftNumber: number;
  shiftWiseAnalysis: boolean;
  utilizationType?: string;
  setAnalyticsData: any;
}

const GridAnalytics = ({
  isLoading,
  assetOverview,
  summary,
  score,
  data,
  assetType,
  aggregation,
  isSelected,
  shiftNumber,
  shiftWiseAnalysis,
  utilizationType,
  setAnalyticsData,
}: Props) => {
  const { t } = useTranslation();
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();
  const { shiftOptions } = useShiftConfig();
  const shift = shiftOptions[shiftNumber - 1];

  const entries = Object.values(score).filter(
    (item): item is any => typeof item === 'object' && 'asset_type' in item
  );

  // Create key-value pairs of asset_type and util_score
  const keyValueObject = entries.reduce<Record<string, number>>((acc, item) => {
    acc[item.asset_type] = item.util_score;
    return acc;
  }, {});
  // Calculate the total average util_score
  const totalSum = entries.reduce(
    (sum, item) => Number(sum) + Number(item.util_score),
    0
  );
  const totalCount = entries.length;
  const totalAverage = totalSum / totalCount;

  // Add the total key to the keyValueObject
  keyValueObject.total = totalAverage;

  const kpiThresholds = getFleetKpiThreshold(assetOverview.kpiThresholds);
  const toSnakeCase = (str: string): string =>
    str.toLowerCase().replace(/\s+/g, '_');
  const thresholdMappings = numberEvaluationKeyMappings(assetType);
  const utilScore = keyValueObject?.[toSnakeCase(assetType)];

  const utilizationScore = evaluateNumber(
    utilScore,
    thresholdMappings.utilization,
    kpiThresholds
  );
  const utilizationChart = data.map((item) => {
    return {
      name: item.date,
      value: item.avg_utilization_engine_by_shift_hours * 100,
    };
  });

  const engineHoursChart = data.map((item) => {
    return {
      name: item.date,
      value: item.avg_utilization_engine_active_minute / 60,
    };
  });

  const idlingHoursChart = data.map((item) => {
    return {
      name: item.date,
      value: item.avg_idling_duration / 3600,
    };
  });

  const productiveHoursChart = data.map((item) => {
    return {
      name: item.date,
      value: item.avg_utilization_engine_working_minute / 60,
    };
  });

  const shiftText = useMemo(() => {
    if (!shift) return '';
    const todayLocal = dayjs().tz(timezone);
    const startTimeStr = formatShiftHour(shift.startHour, todayLocal);
    const endTimeStr = formatShiftHour(shift.endHour, todayLocal);
    const label =
      shift.shiftName ?? t('utilization_shift_label', { number: shiftNumber });
    return `(${label} ${startTimeStr} - ${endTimeStr})`;
  }, [shift, shiftNumber, timezone, t]);

  const periodLabel =
    aggregation?.toLowerCase() === 'daily'
      ? t('utilization_period_daily')
      : aggregation?.toLowerCase() === 'monthly' &&
        ['1Y', 'YTD', 'Custom'].includes(isSelected)
      ? t('utilization_period_monthly')
      : t('utilization_period_daily');

  const buildAvgHoursTitle = (baseKey: string, shiftKey: string) =>
    shiftWiseAnalysis && shiftText
      ? t(shiftKey, { period: periodLabel, shiftText })
      : t(baseKey, { period: periodLabel });

  const tr = useMemo(
    () => [
      {
        title: t('dm_kpi_utilization'),
        pointingUp: !isNegative(
          getDiffForUtilization(summary.utilizationDiff, summary.utilization)
        ),
        value: truncateTo2dp(summary.utilization),
        unit: '%',
        isGreen: !isNegative(
          getDiffForUtilization(summary.utilizationDiff, summary.utilization)
        ),
        analysis: returnAnalysis(
          truncateTo2dp(
            getDiffForUtilization(summary.utilizationDiff, summary.utilization)
          )
        ),
        message: t('from_last_period'),
        hasWarning: false,
        warningCount: 0,
        showProgressBar: true,
        progressBarValue: utilizationScore,
        chartData: utilizationChart,
        isSame:
          getDiffForUtilization(
            summary.utilizationDiff,
            summary.utilization
          ) === 0 && summary.utilization !== 0,
        isNotAvailable:
          !summary.utilizationDiff ||
          summary.utilizationDiff === undefined ||
          summary.utilizationDiff === -100,
      },
      {
        title: buildAvgHoursTitle(
          'utilization_avg_engine_hours_title',
          'utilization_avg_engine_hours_title_shift'
        ),
        pointingUp: !isNegative(
          getDiffForUtilization(
            summary.engineActiveMinutesDiff,
            summary.engineActiveMinutes
          )
        ),
        value: truncateTo2dp(summary.engineActiveMinutes / 60),
        unit: t('utilization_unit_hrs_per_asset'),
        isGreen: !isNegative(
          getDiffForUtilization(
            summary.engineActiveMinutesDiff,
            summary.engineActiveMinutes
          )
        ),
        analysis: returnAnalysis(
          truncateTo2dp(
            getDiffForUtilization(
              summary.engineActiveMinutesDiff,
              summary.engineActiveMinutes
            )
          )
        ),
        message: t('from_last_period'),
        hasWarning: false,
        warningCount: 0,
        showProgressBar: false,
        progressBarValue: 78,
        chartData: engineHoursChart,
        isSame:
          getDiffForUtilization(
            summary.engineActiveMinutesDiff,
            summary.engineActiveMinutes
          ) === 0 && summary.engineActiveMinutes !== 0,
        isNotAvailable:
          !summary.engineActiveMinutesDiff ||
          summary.engineActiveMinutesDiff === undefined ||
          summary.engineActiveMinutesDiff === -100,
      },
      {
        title: buildAvgHoursTitle(
          'utilization_avg_idling_hours_title',
          'utilization_avg_idling_hours_title_shift'
        ),
        pointingUp: !isNegative(
          getDiffForUtilization(summary.idlingDiff, summary.idling)
        ),
        value: truncateTo2dp(convertsecondsToHours(summary?.idling)),
        unit: t('utilization_unit_hrs_per_asset'),
        isGreen: isNegative(
          getDiffForUtilization(summary.idlingDiff, summary.idling)
        ),
        analysis: returnAnalysis(
          truncateTo2dp(
            getDiffForUtilization(summary.idlingDiff, summary.idling)
          )
        ),
        message: t('from_last_period'),
        hasWarning: false,
        warningCount: 0,
        showProgressBar: false,
        progressBarValue: 78,
        chartData: idlingHoursChart,
        isSame:
          getDiffForUtilization(summary.idlingDiff, summary.idling) === 0 &&
          summary.idling !== 0,
        isNotAvailable:
          !summary.idlingDiff ||
          summary.idlingDiff === undefined ||
          summary.idlingDiff === -100,
      },
      {
        title: buildAvgHoursTitle(
          'utilization_avg_productive_hours_title',
          'utilization_avg_productive_hours_title_shift'
        ),
        pointingUp: !isNegative(
          getDiffForUtilization(summary.engineHoursDiff, summary.engineHours)
        ),
        value: truncateTo2dp(summary.engineHours / 60),
        unit: t('utilization_unit_hrs_per_asset'),
        isGreen: !isNegative(
          getDiffForUtilization(summary.engineHoursDiff, summary.engineHours)
        ),
        analysis: returnAnalysis(
          truncateTo2dp(
            getDiffForUtilization(summary.engineHoursDiff, summary.engineHours)
          )
        ),
        message: t('from_last_period'),
        hasWarning: false,
        warningCount: 0,
        showProgressBar: false,
        progressBarValue: 78,
        chartData: productiveHoursChart,
        isSame:
          getDiffForUtilization(
            summary.engineHoursDiff,
            summary.engineHours
          ) === 0 && summary.engineHours !== 0,
        isNotAvailable:
          !summary.engineHoursDiff ||
          summary.engineHoursDiff === undefined ||
          summary.engineHoursDiff === -100,
      },
    ],
    [
      summary,
      aggregation,
      isSelected,
      shiftWiseAnalysis,
      shiftText,
      utilizationScore,
      utilizationChart,
      engineHoursChart,
      idlingHoursChart,
      productiveHoursChart,
      t,
    ]
  );

  useEffect(() => {
    setAnalyticsData((prev: any) =>
      JSON.stringify(prev) !== JSON.stringify(tr) ? tr : prev
    );
  }, [tr, setAnalyticsData]);

  return (
    <Grid
      container
      spacing={2}
      justifyContent="space-between"
      sx={{
        paddingTop: '15px',
      }}
    >
      {tr.map((item: any, index: any) => {
        return (
          <Grid item key={index} lg={3} md={6} sm={12} xs={12}>
            <CardAnalytics
              analytics={item}
              isLoading={isLoading}
              isSelected={isSelected}
              aggregation={aggregation}
              utilizationType={utilizationType}
            />
          </Grid>
        );
      })}
    </Grid>
  );
};

export default GridAnalytics;
